import { useSubsequentEffect } from '@carvana/merch-react-hooks';
import { datadogRum } from '@datadog/browser-rum';
import { useContext, useEffect, useState } from 'react';
import SearchSource from '~/constants/SearchSource';
import {
  useInventoryIsLoaded,
  useSearchRequestId
} from '~/contexts/InventoryContext';
import { useTrackEvent } from '~/contexts/SearchAnalyticsContext';
import SearchEventsDataContext from '~/contexts/SearchEventsDataContext';
import { useLastSearchRequestId, useLoadedInventoryVehicles } from './hooks';
import {
  useTrackMegaSuggestions,
  useTrackMegaVehicle,
  useTrackMegaUserInfo
} from './hooks/track';

function useTrackResults() {
  const { searchSource, keywordSuggestion } = useContext(
    SearchEventsDataContext
  );
  const inventoryIsLoaded = useInventoryIsLoaded();
  const { lastSearchRequestId, updateLastSearchRequestId } =
    useLastSearchRequestId();
  const {
    loadedInventoryVehicles,
    loadedSimilarVehicles,
    shouldHaveSimilarVehicles,
    svSearchRequestId
  } = useLoadedInventoryVehicles();
  const [shouldFire, setShouldFire] = useState(false);
  const searchRequestId = useSearchRequestId();
  const trackEvent = useTrackEvent();
  const trackSuggestions = useTrackMegaSuggestions();
  const trackUserTileInfo = useTrackMegaUserInfo();
  const trackVehicles = useTrackMegaVehicle();

  useEffect(() => {
    if (lastSearchRequestId.current !== searchRequestId) {
      setShouldFire(true);
    }
  }, [lastSearchRequestId, searchRequestId, svSearchRequestId, setShouldFire]);

  useSubsequentEffect(() => {
    const isStillLoading =
      !loadedInventoryVehicles.isLoaded ||
      (!loadedSimilarVehicles.isLoaded && shouldHaveSimilarVehicles) ||
      !inventoryIsLoaded;

    const isSuggestionSource =
      searchSource === SearchSource.TextSearch ||
      searchSource === SearchSource.RecentSearch ||
      searchSource === SearchSource.SavedSearch;

    if (shouldFire && !isStillLoading) {
      trackVehicles();
      trackUserTileInfo.fireEvent();
      trackSuggestions.fireEvent();

      datadogRum.addAction('mega-srp-tile-info', {
        ...trackUserTileInfo.data,
        ...trackSuggestions.data
      });

      updateLastSearchRequestId(searchRequestId);

      if (isSuggestionSource && !loadedInventoryVehicles.vehicles.length) {
        trackEvent('no-results-from-suggestions', {
          suggestionText: keywordSuggestion?.suggestionText,
          source: searchSource
        });
      }

      if (
        shouldHaveSimilarVehicles &&
        lastSearchRequestId.current !== svSearchRequestId
      ) {
        const similarVehiclesData = {
          ...trackUserTileInfo.data,
          ...trackSuggestions.data,
          similarVehiclesSearchRequestId: svSearchRequestId,
          vehicles: loadedSimilarVehicles.vehicles
        };

        trackEvent('tile-info-similar-results', similarVehiclesData);
        datadogRum.addAction('tile-info-similar-results', similarVehiclesData);
        updateLastSearchRequestId(svSearchRequestId);
      }

      setShouldFire(false);
    }
  }, [
    inventoryIsLoaded,
    keywordSuggestion,
    lastSearchRequestId,
    loadedInventoryVehicles,
    loadedSimilarVehicles,
    searchRequestId,
    searchSource,
    setShouldFire,
    shouldFire,
    shouldHaveSimilarVehicles,
    svSearchRequestId,
    updateLastSearchRequestId,
    trackEvent,
    trackSuggestions,
    trackUserTileInfo,
    trackVehicles
  ]);
}

export default useTrackResults;
